import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  Hotel,
  Analytics,
  Assessment,
  Business,
  AttachMoney,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface MarketOverviewSectionProps {
  marketName: string;
  countryName?: string;
  continentName?: string;
  description?: string;
  keyMetrics?: {
    adr?: string;
    revpar?: string;
    occupancy?: string;
    supply?: string;
  };
  showDetailedContent?: boolean;
}

/**
 * Static Market Overview Section for SEO
 * 
 * Purpose: Provide crawlable, keyword-rich content for search engines
 * Benefits:
 * - 100% crawlable static HTML (no JavaScript rendering needed)
 * - Targets long-tail keywords like "[city] hotel market analysis"
 * - Builds topical authority for hospitality market intelligence
 * - Improves rankings for commercial intent searches
 */
export const MarketOverviewSection: React.FC<MarketOverviewSectionProps> = ({
  marketName,
  countryName,
  continentName,
  description,
  keyMetrics,
  showDetailedContent = true,
}) => {
  return (
    <Box component="section" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        {/* SEO-Optimized H1 Heading */}
        <Typography
          component="h1"
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 600,
            mb: 2,
            color: brandColors.blue,
          }}
        >
          {marketName} Hotel Market Analysis & Performance Report
        </Typography>

        {/* Location Breadcrumb for SEO */}
        {(continentName || countryName) && (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            {continentName && <span>{continentName}</span>}
            {continentName && countryName && ' › '}
            {countryName && <span>{countryName}</span>}
            {' › '}
            <strong>{marketName}</strong>
          </Typography>
        )}

        {/* Market Description - Keyword Rich */}
        <Typography
          variant="body1"
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}
        >
          {description || 
            `The ${marketName} hospitality market represents one of the most dynamic hotel and lodging sectors in ${countryName || 'the region'}. Our comprehensive market intelligence covers all hotel segments from luxury to economy, providing investors, developers, and operators with critical performance metrics and strategic insights for informed decision-making.`
          }
        </Typography>

        {/* Key Performance Metrics Grid */}
        {keyMetrics && (
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {keyMetrics.adr && (
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                  <AttachMoney color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    ${keyMetrics.adr}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Daily Rate (ADR)
                  </Typography>
                </Paper>
              </Grid>
            )}
            {keyMetrics.revpar && (
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                  <TrendingUp color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    ${keyMetrics.revpar}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revenue Per Available Room
                  </Typography>
                </Paper>
              </Grid>
            )}
            {keyMetrics.occupancy && (
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                  <Hotel color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {keyMetrics.occupancy}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Occupancy Rate
                  </Typography>
                </Paper>
              </Grid>
            )}
            {keyMetrics.supply && (
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                  <Business color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {keyMetrics.supply}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Room Supply
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {showDetailedContent && (
          <Grid container spacing={4}>
            {/* What's Included Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  What's Included in {marketName} Reports
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Analytics color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Comprehensive KPI Analysis"
                      secondary="Monthly tracking of ADR, RevPAR, occupancy, and demand metrics"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assessment color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Supply & Demand Dynamics"
                      secondary="Detailed analysis of room inventory, pipeline projects, and demand drivers"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Business color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Segmentation Analysis"
                      secondary="Performance by hotel class: Luxury, Upper Upscale, Upscale, Midscale, Economy"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="12-Month Forecasts"
                      secondary="Data-driven projections for market performance and investment opportunities"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Market Intelligence Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  {marketName} Market Intelligence
                </Typography>
                <Typography paragraph>
                  Our {marketName} hotel market reports combine proprietary data sources with on-the-ground insights from our global network of hospitality consultants. Each monthly report provides:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="• Historical performance data dating back to 1990" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Competitive set analysis and benchmarking" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Economic indicators affecting hospitality demand" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Airport traffic and tourism statistics" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Meeting space inventory and group demand" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Investment activity and transaction volumes" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* SEO-Optimized Content Sections */}
        {showDetailedContent && (
          <Box sx={{ mt: 6 }}>
            <Divider sx={{ mb: 4 }} />
            
            {/* About Section for Long-Tail Keywords */}
            <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              About the {marketName} Hospitality Market
            </Typography>
            <Typography paragraph sx={{ lineHeight: 1.8 }}>
              The {marketName} hotel and lodging market serves as a critical component of the regional hospitality industry. 
              With a diverse portfolio of properties ranging from international luxury brands to independent boutique hotels, 
              {marketName} attracts both leisure and business travelers year-round. Our market analysis tracks performance 
              across all major hotel segments, providing stakeholders with the intelligence needed to optimize operations, 
              identify investment opportunities, and understand competitive dynamics.
            </Typography>
            
            <Typography paragraph sx={{ lineHeight: 1.8 }}>
              Recent trends in the {marketName} market include evolving traveler preferences, the impact of alternative 
              accommodations, sustainability initiatives, and technology adoption. Our reports analyze these factors alongside 
              traditional performance metrics to provide a comprehensive view of market conditions and future outlook. 
              Whether you're an investor evaluating acquisition opportunities, a developer planning new construction, or an 
              operator benchmarking performance, our {marketName} market intelligence delivers the insights you need.
            </Typography>

            {/* Why Choose Section */}
            <Typography variant="h4" component="h2" sx={{ mt: 4, mb: 3, fontWeight: 600 }}>
              Why Industry Leaders Choose Our {marketName} Reports
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Trusted Data Sources
                </Typography>
                <Typography paragraph>
                  Our reports synthesize data from STR Global, tourism boards, airport authorities, and proprietary research 
                  to ensure accuracy and completeness.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Local Market Expertise
                </Typography>
                <Typography paragraph>
                  Horwath HTL's consultants in {countryName || 'the region'} provide on-the-ground insights that go beyond 
                  the numbers to explain market dynamics.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Actionable Intelligence
                </Typography>
                <Typography paragraph>
                  Each report includes strategic recommendations and forward-looking analysis to support investment and 
                  operational decisions.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MarketOverviewSection;